import { Briefcase, BookOpen, FolderOpen, Lock, Globe } from 'lucide-react';
import type { DocumentSpace } from '@/shared';

interface SpaceListProps {
  spaces: DocumentSpace[];
  onSelectSpace: (space: DocumentSpace) => void;
}

function getSpaceIcon(slug: string) {
  if (slug.includes('finance')) return <Briefcase size={28} />;
  if (slug.includes('handbook')) return <BookOpen size={28} />;
  return <FolderOpen size={28} />;
}

export function SpaceList({ spaces, onSelectSpace }: SpaceListProps) {
  if (spaces.length === 0) {
    return (
      <div className="glass-panel text-center empty-state animate-fade-in">
        <div className="empty-icon" style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem', color: 'var(--text-muted)' }}>
          <FolderOpen size={48} />
        </div>
        <h3>No Document Spaces Available</h3>
        <p style={{ color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
          You do not have permission to view any document spaces, or no spaces have been configured yet.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="dashboard-title">Document Hub</h2>
      <p className="dashboard-subtitle">Governed access to community folders</p>

      <div className="spaces-grid">
        {spaces.map((space, index) => {
          const rules = space.access_rules || {};
          const isPrivate = (rules.participant_types?.length || 0) > 0 || (rules.roles?.length || 0) > 0;
          return (
            <a
              key={space.id}
              href={`#space-${space.slug}`}
              className="space-card animate-slide-up"
              style={{ animationDelay: `${index * 60}ms` }}
              onClick={(e) => {
                e.preventDefault();
                onSelectSpace(space);
              }}
            >
              <div>
                <div className="space-icon">
                  {getSpaceIcon(space.slug)}
                </div>
                <h3 className="space-name">{space.name}</h3>
                <p className="space-desc">{space.description || 'Access community documents in this workspace.'}</p>
              </div>

              <div className="space-badges-wrapper">
                {!isPrivate ? (
                  <span className="badge badge-public">🌍 Public</span>
                ) : (
                  <>
                    <span className="badge badge-resident" style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem' }}>
                      🔒 Restricted
                    </span>
                    {rules.participant_types?.map((type: string) => (
                      <span key={type} className={`badge ${type === 'resident' ? 'badge-owner' : 'badge-staff'}`} style={{ fontSize: '0.65rem', textTransform: 'none' }}>
                        {type === 'resident' ? 'Resident' : 'Non-Resident'}
                      </span>
                    ))}
                    {rules.roles?.map((role: string) => {
                      const normalizedRole = role.replace(/-/g, '_');
                      let badgeClass = 'badge-verifier';
                      let label = role;
                      if (normalizedRole === 'admin') {
                        badgeClass = 'badge-admin';
                        label = 'Admin';
                      } else if (normalizedRole === 'committee') {
                        badgeClass = 'badge-renter';
                        label = 'Committee';
                      } else if (normalizedRole === 'resident_verifier') {
                        badgeClass = 'badge-verifier';
                        label = 'Verifier';
                      } else {
                        label = role.charAt(0).toUpperCase() + role.slice(1);
                      }
                      return (
                        <span key={role} className={`badge ${badgeClass}`} style={{ fontSize: '0.65rem', textTransform: 'none' }}>
                          {label}
                        </span>
                      );
                    })}
                  </>
                )}
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}
